import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Credentials, LoginError, LoginSession, OtpChallenge } from '../domain/login.model';
import { emptyOtp, initialState } from './login.state';

/**
 * Login slice. Reducers are pure state transitions only; all I/O (the two-step
 * auth + licence fetch) lives in the saga, which dispatches these transitions as
 * it progresses. This is what lets the reducers be unit-tested as `(state,
 * action) => state` with zero mocks.
 */
const slice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    // ── UI edits ──
    credentialsFieldChanged: (
      state,
      action: PayloadAction<{ field: keyof Credentials; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.credentials[field] = value;
      state.credentialsError = null;
    },
    otpDigitChanged: (state, action: PayloadAction<{ index: number; value: string }>) => {
      const { index, value } = action.payload;
      // Keep only the last typed character, mirroring the original page.
      state.otp[index] = value.slice(-1);
      state.otpError = null;
    },
    otpPasted: (state, action: PayloadAction<string[]>) => {
      state.otp = action.payload;
      state.otpError = null;
    },
    backToCredentials: (state) => {
      state.phase = 'credentials';
      state.otp = emptyOtp;
      state.otpToken = null;
      state.otpEmail = null;
      state.otpError = null;
      state.otpStatus = 'idle';
      state.resendStatus = 'idle';
    },
    // ── Credentials lifecycle (saga) ──
    credentialsStarted: (state) => {
      state.credentialsStatus = 'submitting';
      state.credentialsError = null;
      state.retryAfter = 0;
    },
    otpRequired: (state, action: PayloadAction<OtpChallenge>) => {
      state.phase = 'otp';
      state.otpToken = action.payload.otp_token;
      state.otpEmail = action.payload.email;
      state.credentialsStatus = 'succeeded';
      state.otp = emptyOtp;
    },
    credentialsFailed: (
      state,
      action: PayloadAction<{ error: LoginError; retryAfter: number }>,
    ) => {
      state.credentialsStatus = 'failed';
      state.credentialsError = action.payload.error;
      state.retryAfter = action.payload.retryAfter;
    },
    // ── OTP lifecycle (saga) ──
    otpStarted: (state) => {
      state.otpStatus = 'submitting';
      state.otpError = null;
    },
    otpFailed: (state, action: PayloadAction<LoginError>) => {
      state.otpStatus = 'failed';
      state.otpError = action.payload;
      state.otp = emptyOtp; // clear the digits so the user can retype (original behaviour)
    },
    // ── Terminal: both the trusted-device and the OTP-success paths land here ──
    sessionEstablished: (state, action: PayloadAction<LoginSession>) => {
      state.session = action.payload;
      if (state.phase === 'otp') state.otpStatus = 'succeeded';
      else state.credentialsStatus = 'succeeded';
    },
    // ── Resend lifecycle (saga) ──
    resendStarted: (state) => {
      state.resendStatus = 'submitting';
      state.otpError = null;
    },
    resendSucceeded: (state) => {
      state.resendStatus = 'succeeded';
    },
    resendFailed: (state, action: PayloadAction<LoginError>) => {
      state.resendStatus = 'failed';
      state.otpError = action.payload;
    },
    /** Reset to the pristine slice (on unmount). */
    resetForm: () => initialState,
  },
});

export const loginActions = slice.actions;
export const loginReducer = slice.reducer;
